import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Formik, Form} from 'formik';
import {FormattedMessage} from 'react-intl';
import {constants} from '@neosvoc/nv-platform-core-common';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import classNames from 'classnames';
import {requestInstance, requestPostInstance} from '../../actions/action_instance';
import messages from '../../../res/translations/messages';
import Progress from './Progress';
import Content from './Content';
import Error from './Error';
import Layout from '../Layout';
import SubmitButton from '../SubmitButton';
import Alert from '../Alert';
import Spinner from '../Spinner';

const doRedirect = (url) => {
    if ( window.location !== window.top.location ) {
        window.top.postMessage(url, '*');
    } else {
        window.location.replace(url);
    }
};

export class Instance extends Component {
    constructor(props) {
        super(props);
        this.state = {};
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleSubmitClick = this.handleSubmitClick.bind(this);
    }

    static getDerivedStateFromProps(newProps) {
        const {instance} = newProps;
        if (instance) {
            const {content} = instance.page;
            if (content) {
                content.forEach((element) => {
                    const {href, redirect, redirectTimeout} = element;
                    if (redirect && redirectTimeout === 0) {
                        doRedirect(href);
                        return null;
                    }
                });
            }
        }
        return null;
    }

    componentDidMount() {
        this.props.requestInstance(this.props.match.params.id);
    }

    componentDidUpdate(prevProps) {
        if (this.props.match.params.id !== prevProps.match.params.id) {
            this.props.requestInstance(this.props.match.params.id);
        }
        if (prevProps.instance && this.props.instance && prevProps.instance.currentPage !== this.props.instance.currentPage) {
            window.scrollTo(0, 0);
        }
    }

    getAnswers(content) {
        const answers = {};
        content.forEach((element) => {
            if (element.id !== undefined) {
                switch (element.type) {
                    case constants.CONTENT_TYPES.NUMBER:
                        answers[element.id] = '';
                        break;
                    case constants.CONTENT_TYPES.STRING:
                    case constants.CONTENT_TYPES.DATE:
                        answers[element.id] = '';
                        break;
                    case constants.CONTENT_TYPES.SINGLE_CHOICE:
                        answers[element.id] = null;
                        break;
                    case constants.CONTENT_TYPES.MULTIPLE_CHOICE:
                        answers[element.id] = [];
                        break;
                    case constants.CONTENT_TYPES.SINGLE_CHOICE_BATTERY:
                        answers[element.id] = mapValues(keyBy(element.rows, 'id'), () => null);
                        break;
                    case constants.CONTENT_TYPES.MULTIPLE_CHOICE_BATTERY:
                        answers[element.id] = mapValues(keyBy(element.rows, 'id'), () => []);
                        break;
                    case constants.CONTENT_TYPES.MULTIPLE_NUMBER3_BATTERY:
                        answers[element.id] = mapValues(keyBy(element.rows, 'id'), () => '');
                        break;
                }
            }
        });

        return answers;
    }

    renderContent(content, error, values, errors, touched, instanceId) {
        return content.map((c, i) => {
            const contentError = error && error.response && error.response.data && error.response.data.payload &&
                error.response.data.payload.find((field) => field.id === c.id);

            const skipErrorClass = [
                constants.CONTENT_TYPES.MULTIPLE_NUMBER3_BATTERY,
            ].includes(c.type); // These components will handle error border color themselves

            const liClasses = classNames({
                question: true,
                error: !skipErrorClass && ((touched[c.id] && errors[c.id]) || contentError),
            });

            const skipErrorText = [
                constants.CONTENT_TYPES.SINGLE_CHOICE_BATTERY,
                constants.CONTENT_TYPES.MULTIPLE_CHOICE_BATTERY,
                constants.CONTENT_TYPES.MULTIPLE_NUMBER3_BATTERY,
            ].includes(c.type); // These components will handle error text message themselves

            // Some questions span over subsequent Contents, so we need
            // references for back and forth interaction between the two
            const previousContent = content[i-1] ? content[i-1] : null;
            const nextContent = content[i+1] ? content[i+1] : null;

            return (
                <li className={liClasses} key={i}>
                    <Content
                        content={c}
                        value={values[c.id]}
                        instanceId={instanceId}
                        previousContent={previousContent}
                        nextContent={nextContent}
                        doRedirect={doRedirect}
                    />
                    {contentError && <Error {...contentError} />}
                    {touched[c.id] && errors[c.id] && !skipErrorText && <Error {...errors[c.id]} />}
                </li>
            );
        });
    }

    handleSubmit(values) {
        this.props.requestPostInstance(this.props.match.params.id, values);
    }

    handleSubmitClick() {
        setTimeout(() => {
            const firstError = document.querySelector('.input-error-message');
            if (firstError) {
                firstError.parentElement.scrollIntoView();
            }
        });
    }

    render() {
        const {error, instance, submitting} = this.props;

        if (!instance) {
            return <div>Loading...</div>;
        }

        const answers = this.getAnswers(instance.page.content);

        let alert = null;
        if (error && (!error.response || !error.response.data || error.response.data.code !== constants.SERVER_ERRORS.VALIDATION)) {
            alert = (
                <Alert type="danger">
                    {error.message}
                    {error.response && error.response.data && `: ${error.response.data.message}`}
                </Alert>);
        }

        return (
            <Layout progress={<Progress progress={instance.progress} terminal={instance.page.terminal} />}
                alert={alert} terminal={instance.page.terminal} branding={instance.branding}
            >
                <Formik
                    initialValues={answers}
                    enableReinitialize={true}
                    onSubmit={this.handleSubmit}
                    key={instance.currentPage}
                >
                    {({values, errors, touched}) => (
                        <Form className="question-form" noValidate>
                            <ul className="list-unstyled">
                                {this.renderContent(instance.page.content, error, values, errors, touched, instance.id)}
                            </ul>
                            <div className="button-bar">
                                {instance.page.terminal || (
                                    <div className="form-buttons text-sm-right text-center">
                                        <SubmitButton disabled={submitting} onClick={this.handleSubmitClick}>
                                            {submitting ?
                                                <><Spinner /> <FormattedMessage {...messages.instance.loading} /></> :
                                                (instance.page.buttonLabel || <FormattedMessage {...messages.instance.next_button} />)
                                            }
                                        </SubmitButton>
                                    </div>
                                )}
                            </div>
                        </Form>
                    )}
                </Formik>
            </Layout>
        );
    }
}

const mapStateToProps = (state) => ({
    error: state.instance.error,
    instance: state.instance.current,
    submitting: state.instance.submitting,
});

export default connect(mapStateToProps, {requestInstance, requestPostInstance})(Instance);
